/**
 * PATH: packages/email-hub/src/feature/personal/PersonalSourcePill.tsx
 * FILE_ID: EMAILHUB-PERSONAL-SOURCEPILL-001
 * PURPOSE: A tiny "which org did this email come from" pill, rendered in the
 *          EmailRow `mailboxChip` slot on the cross-org Personal inbox.
 * WHY:     FEAT-2026-06-22-CROSS-ORG-PERSONAL — the Personal inbox merges rows
 *          from many orgs into one list, so each row MUST state its source org
 *          or the operator can't tell which mailbox/org a message belongs to.
 * FLOW:    org name (string) → deterministic hue → coloured pill.
 * DEPENDENCIES:
 *   - _internal/lib/utils cn — class merge helper.
 *   - ./orgColors orgHue — stable per-org hue (shared, non-component module).
 */
import { Building2 } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { orgHue } from './orgColors'

interface PersonalSourcePillProps {
  orgId: number
  orgName: string
  onOpen?: () => void
  className?: string
}

export function PersonalSourcePill({
  orgId,
  orgName,
  onOpen,
  className,
}: PersonalSourcePillProps) {
  const content = (
    <>
      <Building2 className="h-3 w-3 shrink-0" />
      <span className="truncate">{orgName}</span>
    </>
  )
  const classes = cn(
    'inline-flex max-w-[12rem] items-center gap-1 rounded-full px-2 py-0.5',
    'text-[11px] font-medium ring-1 ring-inset',
    onOpen && 'hover:brightness-95 focus:outline-none focus:ring-2',
    orgHue(orgId),
    className,
  )

  if (onOpen) {
    return (
      <button
        type="button"
        title={`Switch to ${orgName} and open this copy`}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          onOpen()
        }}
        className={classes}
      >
        {content}
      </button>
    )
  }

  return <span title={`From ${orgName}`} className={classes}>{content}</span>
}
